<template>
  <div>
    <v-toolbar flat color="white">
      <v-toolbar-title>Borç Yönetimi</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Ara (borçlu / ürün / seri)"
        single-line
        hide-details
        clearable
        style="max-width: 300px"
      ></v-text-field>
      <v-btn color="primary" dark class="ml-3" @click="openLend">
        <v-icon left>call_made</v-icon>
        Borç Ver
      </v-btn>
    </v-toolbar>

    <div class="caption grey--text px-3 pb-2">
      Envanteri birine borç/emanet olarak verir; ana envanterden düşer. Geri alındığında stok envantere geri eklenir.
    </div>

    <v-data-table
      :headers="headers"
      :items="filteredLines"
      :loading="loading"
      :rows-per-page-items="[50, 100, 500, { text: 'Tümü', value: -1 }]"
      class="elevation-1"
    >
      <template v-slot:items="props">
        <td class="px-2">{{ props.item.borrowerName }}</td>
        <td class="px-2">
          <v-chip small :color="typeColor(props.item.inventoryType)" text-color="white">
            {{ props.item.inventoryTypeName }}
          </v-chip>
        </td>
        <td class="px-2">{{ props.item.productName }}{{ variantSuffix(props.item) }}</td>
        <td class="px-2">{{ props.item.serialNumber || '-' }}</td>
        <td class="px-2 text-xs-right">{{ props.item.quantity }}</td>
        <td class="px-2 text-xs-right">{{ formatHas(props.item.hasAmount) }}</td>
        <td class="px-2">{{ formatDate(props.item.lentDate) }}</td>
        <td class="px-2">{{ props.item.description || '-' }}</td>
        <td class="px-2">
          <v-chip small :color="props.item.status === 0 ? 'green' : 'grey'" text-color="white">
            {{ props.item.statusName }}
          </v-chip>
        </td>
        <td class="px-2">
          <v-btn v-if="props.item.status === 0" small flat color="red darken-1" @click="openReturn(props.item)">
            <v-icon small left>undo</v-icon>
            Geri Al
          </v-btn>
        </td>
      </template>
      <template v-slot:no-data>
        <div class="text-xs-center pa-4 grey--text">Henüz borç kaydı yok.</div>
      </template>
    </v-data-table>

    <!-- ========== BORÇ VER DIALOG ========== -->
    <v-dialog v-model="lendDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="headline primary white--text">
          <v-icon left color="white">call_made</v-icon>
          Borç Ver
        </v-card-title>
        <v-card-text class="pa-4">
          <v-text-field v-model="lendForm.borrowerName" label="Kime verildi (borçlu)" placeholder="Örn. Ahmet Y. (kuyumcu)" autofocus></v-text-field>
          <v-textarea v-model="lendForm.description" label="Açıklama" rows="2" auto-grow></v-textarea>

          <v-tabs v-model="lendTab" grow class="mb-2">
            <v-tab>Ziynet / Külçe / Diğer</v-tab>
            <v-tab>Takı</v-tab>
          </v-tabs>

          <div v-if="lendTab === 0">
            <v-select
              v-model="lendForm.inventoryItemId"
              :items="adetItems"
              item-text="label"
              item-value="id"
              label="Ürün"
            ></v-select>
            <div v-if="selectedInventoryItem" class="caption grey--text mb-2">
              Ana envanterde boşta: <strong>{{ availableOf(selectedInventoryItem) }}</strong> adet
            </div>
            <v-text-field v-model.number="lendForm.quantity" label="Adet" type="number" min="1"></v-text-field>
          </div>

          <div v-else>
            <v-text-field
              v-model="lendForm.barcode"
              label="Takı Barkodu / Seri No"
              prepend-icon="qr_code_scanner"
              hint="Sertifikadaki barkodu okutun veya yazın"
              persistent-hint
              @keyup.enter="saveLend"
            ></v-text-field>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="lendDialog = false">İptal</v-btn>
          <v-btn color="primary" dark :loading="lendSaving" @click="saveLend">Borç Ver</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ========== GERİ AL DIALOG (adet) ========== -->
    <v-dialog v-model="returnDialog" max-width="460" persistent>
      <v-card>
        <v-card-title class="headline red darken-1 white--text">
          <v-icon left color="white">undo</v-icon>
          Ana Envantere Geri Al
        </v-card-title>
        <v-card-text class="pa-4">
          <div class="mb-2">
            <strong>{{ returnTarget && returnTarget.borrowerName }}</strong> →
            {{ returnTarget && returnTarget.productName }}{{ variantSuffix(returnTarget) }}
          </div>
          <v-text-field
            v-model.number="returnQuantity"
            label="Geri alınacak adet"
            type="number"
            min="1"
            :max="returnTarget ? returnTarget.quantity : 1"
          ></v-text-field>
          <div class="caption grey--text">Borçta: {{ returnTarget ? returnTarget.quantity : 0 }} adet</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="returnDialog = false">İptal</v-btn>
          <v-btn color="red darken-1" dark :loading="returnSaving" @click="confirmReturn">Geri Al</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "BorcYonetimi",
  data: () => ({
    loading: false,
    lines: [],
    inventoryItems: [],
    zimmetLines: [],
    search: "",
    headers: [
      { text: "Borçlu", value: "borrowerName" },
      { text: "Tip", value: "inventoryTypeName" },
      { text: "Ürün", value: "productName" },
      { text: "Seri No", value: "serialNumber" },
      { text: "Adet", value: "quantity", align: "right" },
      { text: "Has", value: "hasAmount", align: "right" },
      { text: "Tarih", value: "lentDate" },
      { text: "Açıklama", value: "description" },
      { text: "Durum", value: "status" },
      { text: "", value: "actions", sortable: false },
    ],

    lendDialog: false,
    lendSaving: false,
    lendTab: 0,
    lendForm: { borrowerName: "", description: "", inventoryItemId: null, quantity: 1, barcode: "" },

    returnDialog: false,
    returnSaving: false,
    returnTarget: null,
    returnQuantity: 1,
  }),

  computed: {
    adetItems() {
      return this.inventoryItems
        .filter((i) => i.inventoryType !== 0) // 0 = Takı
        .map((i) => ({
          ...i,
          label: `${i.inventoryTypeName} - ${i.productName}${this.variantSuffix(i)} (${i.weight} gr, ${i.purityLabel}) — boşta ${this.availableOf(i)}`,
        }));
    },
    selectedInventoryItem() {
      return this.inventoryItems.find((i) => i.id === this.lendForm.inventoryItemId) || null;
    },
    filteredLines() {
      const q = (this.search || "").toLocaleLowerCase("tr");
      if (!q) return this.lines;
      return this.lines.filter((l) =>
        [l.borrowerName, l.productName, l.serialNumber, l.inventoryTypeName, l.description]
          .filter(Boolean)
          .some((v) => v.toLocaleLowerCase("tr").includes(q))
      );
    },
  },

  created() {
    this.loadAll();
  },

  methods: {
    variantSuffix(i) {
      if (!i) return "";
      const parts = [i.kulp, i.dateType].filter(Boolean);
      return parts.length ? ` [${parts.join(" / ")}]` : "";
    },
    typeColor(t) {
      if (t === 0) return "teal";
      if (t === 1) return "amber darken-2";
      return "blue-grey darken-1";
    },
    formatHas(v) {
      if (v == null || Number(v) === 0) return "-";
      return Number(v).toLocaleString("tr-TR", { maximumFractionDigits: 4 });
    },
    formatDate(d) {
      if (!d) return "-";
      return new Date(d).toLocaleDateString("tr-TR");
    },
    // boşta = toplam Quantity − zimmetli (borç zaten Quantity'i düşürdüğü için canlı doğru)
    availableOf(item) {
      if (!item) return 0;
      const assigned = this.zimmetLines
        .filter((l) => l.inventoryItemId === item.id)
        .reduce((s, l) => s + (l.quantity || 0), 0);
      return item.quantity - assigned;
    },

    async loadAll() {
      this.loading = true;
      try {
        const [linesRes, itemsRes, zimmetRes] = await Promise.all([
          this.$store.dispatch("inventory/getBorclar"),
          this.$store.dispatch("inventory/getInventoryItems"),
          this.$store.dispatch("inventory/getAllZimmets"),
        ]);
        this.lines = linesRes || [];
        this.inventoryItems = itemsRes || [];
        this.zimmetLines = zimmetRes || [];
      } catch (e) {
        this.notify("Borç verileri yüklenemedi: " + this.errMsg(e));
      } finally {
        this.loading = false;
      }
    },

    openLend() {
      this.lendForm = { borrowerName: "", description: "", inventoryItemId: null, quantity: 1, barcode: "" };
      this.lendTab = 0;
      this.lendDialog = true;
    },

    async saveLend() {
      const borrower = (this.lendForm.borrowerName || "").trim();
      if (!borrower) {
        this.notify("Borçlu adı girin.");
        return;
      }
      let dto;
      if (this.lendTab === 1) {
        const code = (this.lendForm.barcode || "").trim().replace(/\*/g, "-"); // okuyucu "-"yi "*" gönderebilir
        if (!code) { this.notify("Takı barkodu okutun."); return; }
        dto = { borrowerName: borrower, description: this.lendForm.description, barcode: code };
      } else {
        if (!this.lendForm.inventoryItemId) { this.notify("Ürün seçin."); return; }
        if (!this.lendForm.quantity || this.lendForm.quantity <= 0) { this.notify("Adet 0'dan büyük olmalı."); return; }
        dto = {
          borrowerName: borrower,
          description: this.lendForm.description,
          inventoryItemId: this.lendForm.inventoryItemId,
          quantity: this.lendForm.quantity,
        };
      }
      this.lendSaving = true;
      try {
        const res = await this.$store.dispatch("inventory/borcLend", dto);
        if (res && res.success === false) { this.notify(res.message); return; }
        if (this.lendTab === 1) this.lendForm.barcode = "";
        else this.lendDialog = false;
        this.notify((res && res.message) || "Borç verildi.");
        await this.loadAll();
      } catch (e) {
        this.notify("Hata: " + this.errMsg(e));
      } finally {
        this.lendSaving = false;
      }
    },

    openReturn(line) {
      this.returnTarget = line;
      if (line.inventoryType === 0) {
        this.doReturn(line, 0);
        return;
      }
      this.returnQuantity = line.quantity;
      this.returnDialog = true;
    },

    async confirmReturn() {
      if (!this.returnTarget) return;
      if (!this.returnQuantity || this.returnQuantity <= 0) { this.notify("Adet 0'dan büyük olmalı."); return; }
      await this.doReturn(this.returnTarget, this.returnQuantity);
      this.returnDialog = false;
    },

    async doReturn(line, quantity) {
      this.returnSaving = true;
      try {
        const res = await this.$store.dispatch("inventory/borcReturn", { borcId: line.id, quantity });
        if (res && res.success === false) { this.notify(res.message); return; }
        this.notify((res && res.message) || "Ana envantere geri alındı.");
        await this.loadAll();
      } catch (e) {
        this.notify("Hata: " + this.errMsg(e));
      } finally {
        this.returnSaving = false;
      }
    },

    errMsg(e) {
      return (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || "Bilinmeyen hata";
    },
    notify(msg) {
      this.$store.state.dialog = true;
      this.$store.state.message = msg;
    },
  },
};
</script>
